/**
 * /checkout/success — Stripe Checkout return (success). PRD-003 PR-03, T-028.
 *
 * Stripe redirects here with `?session_id=cs_...` after a completed Checkout.
 * The subscriber record is NOT created here. The authoritative state change
 * happens server-side when Stripe delivers `checkout.session.completed` then
 * `customer.subscription.created` to the webhook (T-029), which creates the
 * subscriber row + onboarding state and sends the onboarding email. This page
 * is a holding pattern: it confirms the payment landed and tells the
 * subscriber the onboarding link is on its way by email.
 *
 * Voice: Eric's client-communication register — warm, plain, no chrome. No
 * dashboard yet because there is intentionally nothing for them to do until
 * onboarding (PR-06) and Eric's first-touch email (PR-07).
 */

import { createFileRoute } from '@tanstack/react-router';

interface CheckoutSuccessSearch {
  session_id?: string;
}

function validateSearch(
  search: Record<string, unknown>,
): CheckoutSuccessSearch {
  return typeof search.session_id === 'string'
    ? { session_id: search.session_id }
    : {};
}

export const Route = createFileRoute('/checkout/success')({
  validateSearch,
  component: CheckoutSuccessPage,
});

function CheckoutSuccessPage() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6 py-16">
      <div className="w-full max-w-lg space-y-6">
        <h1 className="font-display text-3xl text-foreground">
          You are in. Thank you.
        </h1>
        <div className="space-y-4 text-base leading-relaxed text-foreground">
          <p>
            Your subscription is active. The next step is a short onboarding so
            I understand your company, your audience, and what is actually in
            your way right now.
          </p>
          <p>
            Check your email. There is an onboarding link waiting for you. It
            takes about fifteen minutes and there are no meetings involved.
          </p>
          <p>
            Once you have filled it in, I will read it myself and send you a
            real reply, not a templated welcome, before any task starts.
          </p>
        </div>
        <p className="text-sm text-muted-foreground">
          If the email has not arrived in a few minutes, check spam, then reach
          me through the{' '}
          <a
            href="https://yesplease.studio/contact"
            className="text-primary underline underline-offset-4"
          >
            contact form
          </a>{' '}
          and I will sort it out.
        </p>
      </div>
    </main>
  );
}
